import React, { useEffect, useState } from 'react';
import { X, UserPlus, Wallet, PartyPopper, CheckCircle2, MousePointerClick, Bell, XCircle } from 'lucide-react';
import useRequest from './hooks/use-req';

interface ApiNotification {
  id: number;
  type: string;
  title: string;
  message: string;
  data?: Record<string, any>;
  is_read: boolean;
  created_at: string;
  read_at: string | null;
}

interface NotificationTypeStyle {
  icon: React.ReactNode;
  iconBg: string;
}

const typeStyles: Record<string, NotificationTypeStyle> = {
  link_click: { icon: <MousePointerClick size={18} className="text-[#6661FF]" />, iconBg: 'bg-[#EEEDFF]' },
  referral_joined: { icon: <UserPlus size={18} className="text-[#00A86B]" />, iconBg: 'bg-[#E5F6F0]' },
  reward_earned: { icon: <Wallet size={18} className="text-[#6661FF]" />, iconBg: 'bg-[#EEEDFF]' },
  payout_processing: { icon: <PartyPopper size={18} className="text-[#D68400]" />, iconBg: 'bg-[#FFF4E5]' },
  payout_successful: { icon: <CheckCircle2 size={18} className="text-[#00A86B]" />, iconBg: 'bg-[#E5F6F0]' },
  reward_declined: { icon: <XCircle size={18} className="text-[#C62828]" />, iconBg: 'bg-[#FDECEC]' },
};

const defaultTypeStyle: NotificationTypeStyle = {
  icon: <Bell size={18} className="text-[#8494AE]" />,
  iconBg: 'bg-gray-100',
};

const formatTimestamp = (value: string) => {
  const date = new Date(value);
  if (isNaN(date.getTime())) return value;
  const datePart = date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const timePart = date.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
  return `${datePart} · ${timePart}`;
};

interface NotificationsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onUnreadCountChange?: (count: number) => void;
}

const NotificationsDrawer: React.FC<NotificationsDrawerProps> = ({ isOpen, onClose, onUnreadCountChange }) => {
  const authToken = localStorage.getItem('authToken');
  const [notifications, setNotifications] = useState<ApiNotification[]>([]);
  const [markingId, setMarkingId] = useState<number | null>(null);

  const { makeRequest: getNotifications, loading: loadingList } = useRequest('/notifications/', 'GET', {
    Authorization: authToken ? `Bearer ${authToken}` : undefined,
  });

  const { makeRequest: markOneAsRead } = useRequest('/notifications', 'PATCH', {
    Authorization: authToken ? `Bearer ${authToken}` : undefined,
  });

  const { makeRequest: readAllNotifications, loading: markingAll } = useRequest('/notifications/read-all', 'PATCH', {
    Authorization: authToken ? `Bearer ${authToken}` : undefined,
  });


  useEffect(() => {
    if (!isOpen) return;

    const fetchData = async () => {
      const [response] = await getNotifications(undefined, { limit: 20, offset: 0 });
      if (response?.notifications) {
        setNotifications(response.notifications);
        onUnreadCountChange?.(response.unread_count || 0);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const countUnread = (list: ApiNotification[]) => list.filter((item) => !item.is_read).length;

  const handleMarkOneRead = async (item: ApiNotification) => {
    if (item.is_read || markingId) return;

    setMarkingId(item.id);
    const [response] = await markOneAsRead(undefined, undefined, `${item.id}/read`);
    setMarkingId(null);

    if (response?.status !== false) {
      setNotifications((prev) => {
        const updated = prev.map((n) => (n.id === item.id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n));
        onUnreadCountChange?.(countUnread(updated));
        return updated;
      });
    }
  };

  const handleMarkAllRead = async () => {
    const [response] = await readAllNotifications();

    if (response?.status !== false) {
      setNotifications((prev) => prev.map((item) => ({ ...item, is_read: true })));
      onUnreadCountChange?.(0);
    }
  };

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/50 z-[60] transition-opacity duration-300 ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
        onClick={onClose}
      />

      <div
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-white z-[70] shadow-xl flex flex-col transition-transform duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
      >
        <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100">
          <div>
            <h2 className="text-[18px] font-semibold text-[#121212]">Notifications</h2>
            <p className="text-[13px] text-[#8494AE] mt-1">
              Updates on referrals, rewards, and activity
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close notifications"
            className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 flex-shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {loadingList && (
            <p className="text-sm text-gray-400 text-center py-10">Loading notifications...</p>
          )}

          {!loadingList && notifications.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-10">No notifications yet.</p>
          )}

          {!loadingList && notifications.map((item) => {
            const style = typeStyles[item.type] || defaultTypeStyle;
            return (
              <div
                key={item.id}
                onClick={() => handleMarkOneRead(item)}
                className={`flex items-start gap-3 rounded-2xl border p-4 transition-opacity ${item.is_read ? 'border-gray-100 bg-white' : 'border-[#DBF3E6] bg-[#F7FDFA] cursor-pointer'
                  } ${markingId === item.id ? 'opacity-60' : ''}`}
              >
                <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${style.iconBg}`}>
                  {style.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-semibold text-[#121212]">{item.title}</p>
                  <p className="text-[13px] text-[#65799A] mt-0.5">{item.message}</p>
                  <p className="text-[12px] text-[#8494AE] mt-1.5">{formatTimestamp(item.created_at)}</p>
                </div>
                {!item.is_read && (
                  <span className="w-2 h-2 rounded-full bg-[#00A86B] flex-shrink-0 mt-1.5" />
                )}
              </div>
            );
          })}
        </div>

        <div className="px-6 py-4 border-t border-gray-100">
          <button
            onClick={handleMarkAllRead}
            disabled={markingAll}
            className="w-full bg-black text-white rounded-full py-3 text-sm font-medium hover:bg-gray-900 transition-colors disabled:opacity-60"
          >
            {markingAll ? 'Marking as read...' : 'Mark all as read'}
          </button>
        </div>
      </div>
    </>
  );
};

export default NotificationsDrawer;
